import { CanvasTexture, RepeatWrapping, SRGBColorSpace } from "three";

/**
 * Runtime canvas textures shared by the hero solar system and the /sys
 * fly page. ZERO network downloads — every planet surface, ring, and
 * glow is painted at mount (~1ms each), the same CanvasTexture trick as
 * the Anatomy plate labels. Extracted from SolarSystemScene so both
 * scenes render identical worlds.
 */

/* ── deterministic rand for canvas painting ─────────────────────────── */

export const makeRand = (seed: number) => {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
};

export const makeCanvas = (w: number, h: number) => {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  return { canvas, ctx: canvas.getContext("2d")! };
};

export const toTexture = (canvas: HTMLCanvasElement, wrap = false) => {
  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  if (wrap) tex.wrapS = RepeatWrapping;
  return tex;
};

/** Horizontal latitude bands — the backbone of every planet surface. */
export const bands = (
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  stops: Array<[number, string]>,
) => {
  const g = ctx.createLinearGradient(0, 0, 0, h);
  for (const [p, c] of stops) g.addColorStop(p, c);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
};

/** Soft elliptical splotches — continents, storms, albedo patches. */
export const splotch = (
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  rand: () => number,
  n: number,
  colors: string[],
  alpha: number,
  rx: [number, number],
  yBand: [number, number] = [0.12, 0.88],
) => {
  ctx.globalAlpha = alpha;
  for (let i = 0; i < n; i++) {
    ctx.fillStyle = colors[Math.floor(rand() * colors.length)];
    const x = rand() * w;
    const y = h * (yBand[0] + rand() * (yBand[1] - yBand[0]));
    const r = w * (rx[0] + rand() * (rx[1] - rx[0]));
    ctx.beginPath();
    ctx.ellipse(x, y, r, r * (0.35 + rand() * 0.4), 0, 0, Math.PI * 2);
    ctx.fill();
    // wrap horizontally so the seam never shows a cut continent
    if (x + r > w) {
      ctx.beginPath();
      ctx.ellipse(x - w, y, r, r * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
};

export const PAINTERS: Record<string, (ctx: CanvasRenderingContext2D, w: number, h: number) => void> = {
  mercury: (ctx, w, h) => {
    const rand = makeRand(11);
    bands(ctx, w, h, [[0, "#6e6259"], [0.5, "#9c8d80"], [1, "#5c5249"]]);
    splotch(ctx, w, h, rand, 60, ["#4f463e", "#7d7066", "#b3a496"], 0.35, [0.01, 0.04]);
  },
  venus: (ctx, w, h) => {
    const rand = makeRand(22);
    bands(ctx, w, h, [
      [0, "#c9a96a"], [0.3, "#e8cf96"], [0.55, "#d9b878"], [0.8, "#eed9a8"], [1, "#bfa066"],
    ]);
    splotch(ctx, w, h, rand, 16, ["#f2e2b8", "#cdab6e"], 0.22, [0.08, 0.2]);
  },
  // real continent silhouettes — paintEarthDetailed is defined below and
  // invoked at paint time (after module init), so the forward ref is safe
  earth: (ctx, w, h) => paintEarthDetailed(ctx, w, h),
  mars: (ctx, w, h) => {
    const rand = makeRand(44);
    bands(ctx, w, h, [[0, "#9a4a2c"], [0.5, "#c1683c"], [1, "#8a3f26"]]);
    splotch(ctx, w, h, rand, 40, ["#6e3018", "#d98a55"], 0.3, [0.02, 0.07]);
    // southern polar cap
    bands(ctx, w, h, [[0.9, "rgba(255,250,240,0)"], [1, "rgba(255,250,240,0.85)"]]);
  },
  jupiter: (ctx, w, h) => {
    const rand = makeRand(55);
    bands(ctx, w, h, [
      [0, "#b89a78"], [0.18, "#dcc3a0"], [0.3, "#a87c5a"], [0.42, "#e8d6b4"],
      [0.52, "#c19a72"], [0.62, "#ead9ba"], [0.74, "#ab835e"], [0.88, "#d4bb96"], [1, "#a08362"],
    ]);
    splotch(ctx, w, h, rand, 20, ["#f0e2c4", "#96714e"], 0.18, [0.04, 0.12]);
    // Great Red Spot
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = "#c4593a";
    ctx.beginPath();
    ctx.ellipse(w * 0.68, h * 0.63, w * 0.055, h * 0.07, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  },
  saturn: (ctx, w, h) => {
    bands(ctx, w, h, [
      [0, "#c2a878"], [0.2, "#e2cda2"], [0.35, "#cdb285"], [0.5, "#ecdcb4"],
      [0.65, "#d6bd90"], [0.82, "#e6d2a6"], [1, "#b89e72"],
    ]);
  },
  uranus: (ctx, w, h) => {
    bands(ctx, w, h, [[0, "#7fc4cc"], [0.5, "#a8dde2"], [1, "#74b8c2"]]);
  },
  neptune: (ctx, w, h) => {
    const rand = makeRand(88);
    bands(ctx, w, h, [[0, "#2540a8"], [0.45, "#3a5cd0"], [0.7, "#2d4ab8"], [1, "#1e3490"]]);
    splotch(ctx, w, h, rand, 6, ["#16297a"], 0.5, [0.05, 0.1]);
    splotch(ctx, w, h, rand, 8, ["#7d96ec"], 0.25, [0.04, 0.1]);
  },
};

/** Grey cratered surface for moons — one painter, seeded per moon. */
export const paintMoon = (seed: number) => (ctx: CanvasRenderingContext2D, w: number, h: number) => {
  const rand = makeRand(seed);
  bands(ctx, w, h, [[0, "#8d8d92"], [0.5, "#b5b3ae"], [1, "#7e7d82"]]);
  splotch(ctx, w, h, rand, 50, ["#6a6a70", "#9b9892", "#c8c5be"], 0.4, [0.015, 0.05]);
};

/** Soft radial glow — used for the Sun corona and planet atmospheres. */
export const makeGlowTexture = (inner: string, outer: string) => {
  const { canvas, ctx } = makeCanvas(128, 128);
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  g.addColorStop(0, inner);
  g.addColorStop(0.35, outer);
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  return toTexture(canvas);
};

/** Saturn's rings — translucent concentric bands with the Cassini gap. */
export const makeRingTexture = () => {
  const { canvas, ctx } = makeCanvas(256, 256);
  const g = ctx.createRadialGradient(128, 128, 60, 128, 128, 128);
  g.addColorStop(0.0, "rgba(0,0,0,0)");
  g.addColorStop(0.52, "rgba(0,0,0,0)");
  g.addColorStop(0.58, "rgba(214,189,144,0.55)");
  g.addColorStop(0.68, "rgba(232,212,168,0.8)");
  g.addColorStop(0.73, "rgba(120,100,70,0.15)"); // Cassini division
  g.addColorStop(0.78, "rgba(226,204,158,0.7)");
  g.addColorStop(0.9, "rgba(196,172,128,0.4)");
  g.addColorStop(1.0, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
  return toTexture(canvas);
};

/** The Milky Way — photographic, not painterly: the band's light comes
    from THOUSANDS of fine stars with gaussian falloff around the
    galactic plane, plus a faint spine glow, a galactic-center bulge,
    and elongated dark dust lanes. High-res (2048×1024) so nothing
    stretches into smudges on the sky sphere. Painted once at runtime —
    still zero downloads. */
export const makeMilkyWayTexture = () => {
  const W = 2048;
  const H = 1024;
  const { canvas, ctx } = makeCanvas(W, H);
  const rand = makeRand(777);
  const mid = H / 2;
  const bandHalf = H * 0.058; // tight band — ±~10° of galactic latitude

  // 1) faint smooth spine — low alpha so it never reads as a smear
  const spine = ctx.createLinearGradient(0, mid - bandHalf * 3, 0, mid + bandHalf * 3);
  spine.addColorStop(0, "rgba(170,180,215,0)");
  spine.addColorStop(0.42, "rgba(190,198,230,0.06)");
  spine.addColorStop(0.5, "rgba(215,220,242,0.11)");
  spine.addColorStop(0.58, "rgba(190,198,230,0.06)");
  spine.addColorStop(1, "rgba(170,180,215,0)");
  ctx.fillStyle = spine;
  ctx.fillRect(0, mid - bandHalf * 3, W, bandHalf * 6);

  // 2) galactic-center bulge — the band really is brighter toward
  //    Sagittarius; one warm wide glow, still subtle
  const bulge = ctx.createRadialGradient(W * 0.3, mid, 0, W * 0.3, mid, W * 0.11);
  bulge.addColorStop(0, "rgba(235,220,195,0.13)");
  bulge.addColorStop(0.5, "rgba(215,200,180,0.06)");
  bulge.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = bulge;
  ctx.fillRect(W * 0.3 - W * 0.11, mid - W * 0.11, W * 0.22, W * 0.22);

  // 3) the band itself: ~9000 fine stars, gaussian-packed to the plane,
  //    denser toward the bulge, warm/cool tinted
  for (let i = 0; i < 9000; i++) {
    const x = rand() * W;
    const yOff = (rand() + rand() + rand() - 1.5) / 1.5; // ~gaussian −1..1
    const y = mid + yOff * bandHalf * 2.6;
    const planeDensity = Math.exp(-yOff * yOff * 3.2);
    const bulgeBoost = 0.55 + 0.45 * Math.exp(-(((x - W * 0.3) / (W * 0.28)) ** 2));
    if (rand() > planeDensity * bulgeBoost) continue;
    const warm = rand() > 0.5;
    const a = (0.07 + rand() * 0.45) * planeDensity;
    ctx.fillStyle = warm
      ? `rgba(240,228,205,${a})`
      : `rgba(205,215,245,${a})`;
    const px = rand() > 0.92 ? 2 : 1;
    ctx.fillRect(x, y, px, px);
  }

  // 4) small unresolved knots inside the band — tiny, plentiful, dim
  for (let i = 0; i < 380; i++) {
    const x = rand() * W;
    const y = mid + (rand() + rand() - 1) * bandHalf * 0.9;
    const r = 2.5 + rand() * 6;
    const kg = ctx.createRadialGradient(x, y, 0, x, y, r);
    kg.addColorStop(0, `rgba(225,225,242,${0.05 + rand() * 0.05})`);
    kg.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = kg;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }

  // 5) dust lanes — long dark streaks ALONG the band (this is what makes
  //    it read as a galaxy edge-on, not a glow stripe)
  ctx.globalCompositeOperation = "destination-out";
  for (let i = 0; i < 70; i++) {
    const x = rand() * W;
    const y = mid + (rand() - 0.5) * bandHalf * 1.1;
    const len = 60 + rand() * 320;
    const thick = 3 + rand() * 9;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate((rand() - 0.5) * 0.1); // nearly parallel to the plane
    const dg = ctx.createLinearGradient(-len, 0, len, 0);
    dg.addColorStop(0, "rgba(0,0,0,0)");
    dg.addColorStop(0.5, `rgba(0,0,0,${0.18 + rand() * 0.3})`);
    dg.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = dg;
    ctx.beginPath();
    ctx.ellipse(0, 0, len, thick, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  ctx.globalCompositeOperation = "source-over";

  return toTexture(canvas);
};


/* ── realistic Earth (hero foreground) ──────────────────────────────── */

/** Rough real-continent outlines as (lon, lat) polygons — recognizable
    silhouettes, not survey data. Shared by the day map and city lights. */
const CONTINENTS: Array<Array<[number, number]>> = [
  // North America
  [[-168, 66], [-150, 70], [-128, 70], [-120, 60], [-130, 55], [-125, 40], [-117, 32], [-105, 20], [-97, 15], [-83, 8], [-80, 25], [-75, 35], [-65, 45], [-55, 50], [-70, 58], [-80, 62], [-95, 70], [-110, 68], [-140, 69], [-168, 66]],
  // South America
  [[-80, 8], [-75, -5], [-70, -20], [-70, -35], [-71, -50], [-68, -55], [-58, -38], [-48, -25], [-35, -8], [-50, 0], [-60, 8], [-80, 8]],
  // Greenland
  [[-55, 60], [-30, 68], [-20, 76], [-35, 83], [-60, 76], [-55, 60]],
  // Africa
  [[-17, 21], [-15, 11], [-5, 5], [8, 4], [12, -8], [14, -22], [18, -34], [28, -32], [35, -25], [40, -12], [51, 10], [43, 12], [32, 30], [10, 34], [-6, 35], [-17, 21]],
  // Eurasia
  [[-9, 43], [-9, 36], [3, 37], [15, 38], [23, 36], [33, 36], [45, 40], [50, 30], [60, 25], [70, 20], [77, 8], [80, 15], [88, 22], [98, 8], [105, 10], [108, 18], [120, 30], [122, 38], [135, 45], [160, 58], [170, 62], [160, 68], [140, 72], [110, 76], [90, 76], [60, 70], [45, 67], [30, 70], [20, 62], [8, 56], [2, 51], [-9, 43]],
  // Indonesia / SE Asia islands
  [[95, 5], [105, 2], [115, -3], [125, -7], [135, -5], [128, -2], [115, 0], [103, 5], [95, 5]],
  // Australia
  [[114, -22], [122, -18], [135, -12], [142, -11], [150, -22], [153, -30], [146, -39], [135, -35], [124, -33], [114, -26], [114, -22]],
  // Antarctica (band)
  [[-180, -70], [-120, -73], [-60, -71], [0, -69], [60, -72], [120, -70], [180, -71], [180, -90], [-180, -90], [-180, -70]],
];

/** Megacity clusters for the night-side lights. (lon, lat, strength) */
const CITY_CLUSTERS: Array<[number, number, number]> = [
  [-74, 41, 1], [-87, 42, 0.8], [-118, 34, 0.9], [-95, 30, 0.7], [-80, 26, 0.6],
  [-99, 19, 0.7], [-43, -23, 0.7], [-58, -34, 0.6], [-70, -33, 0.4],
  [-2, 51, 1], [2, 49, 0.9], [13, 52, 0.8], [12, 45, 0.7], [-4, 40, 0.6], [28, 41, 0.7], [37, 56, 0.7],
  [31, 30, 0.7], [3, 6, 0.5], [28, -26, 0.5],
  [72, 19, 0.9], [77, 28, 0.9], [88, 22, 0.7], [80, 13, 0.6],
  [104, 1, 0.6], [100, 14, 0.6], [114, 22, 0.9], [121, 31, 1], [117, 39, 0.8], [127, 37, 0.9], [139, 35, 1], [135, 34, 0.8],
  [151, -34, 0.6], [145, -38, 0.5],
];

const lonLatToXY = (lon: number, lat: number, w: number, h: number): [number, number] => [
  ((lon + 180) / 360) * w,
  ((90 - lat) / 180) * h,
];

const tracePolygon = (
  ctx: CanvasRenderingContext2D,
  poly: Array<[number, number]>,
  w: number,
  h: number,
  jitter: number,
  rand: () => number,
) => {
  ctx.beginPath();
  poly.forEach(([lon, lat], i) => {
    const [x, y] = lonLatToXY(lon, lat, w, h);
    const jx = x + (rand() - 0.5) * jitter;
    const jy = y + (rand() - 0.5) * jitter;
    if (i === 0) ctx.moveTo(jx, jy);
    else ctx.lineTo(jx, jy);
  });
  ctx.closePath();
};

/** Day map: graded oceans, real continent silhouettes with latitude
    tinting (tundra → temperate → desert → tropics), polar ice. */
export const paintEarthDetailed = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
  const rand = makeRand(3344);

  // ocean — deep basins with subtle banding
  bands(ctx, w, h, [
    [0, "#10306e"], [0.22, "#14418c"], [0.5, "#1a4f9e"], [0.78, "#14418c"], [1, "#0e2a60"],
  ]);
  splotch(ctx, w, h, rand, 30, ["#0e2f6e", "#1f5cae"], 0.18, [0.05, 0.14]);

  // land — fill each continent, then tint by latitude
  const jitter = w * 0.008;
  for (const poly of CONTINENTS) {
    tracePolygon(ctx, poly, w, h, jitter, rand);
    ctx.fillStyle = "#4a7a40";
    ctx.fill();
  }
  // latitude tinting clipped to land: deserts ~±20–30°, tundra toward poles
  ctx.save();
  for (const poly of CONTINENTS) tracePolygon(ctx, poly, w, h, jitter, rand);
  ctx.clip();
  bands(ctx, w, h, [
    [0, "rgba(225,232,238,0.85)"],   // arctic
    [0.16, "rgba(120,140,90,0.25)"],
    [0.3, "rgba(150,140,80,0.3)"],
    [0.38, "rgba(196,168,100,0.5)"], // northern deserts
    [0.5, "rgba(70,120,60,0.35)"],   // tropics
    [0.62, "rgba(180,150,95,0.45)"], // southern deserts
    [0.78, "rgba(120,135,95,0.25)"],
    [1, "rgba(232,238,243,0.9)"],    // antarctic
  ]);
  // terrain grain
  splotch(ctx, w, h, rand, 160, ["#3d6636", "#6b8a4d", "#8a7a4a", "#5a7444"], 0.25, [0.004, 0.018]);
  ctx.restore();
};

/** Night-side city lights — warm pinpricks clustered on real megacity
    coordinates, used as an emissive map so the dark limb glows. */
export const makeEarthLightsTexture = (w = 1024, h = 512) => {
  const { canvas, ctx } = makeCanvas(w, h);
  const rand = makeRand(4455);
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, w, h);
  for (const [lon, lat, strength] of CITY_CLUSTERS) {
    const [cx, cy] = lonLatToXY(lon, lat, w, h);
    const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, 10 + strength * 14);
    halo.addColorStop(0, `rgba(255,196,120,${0.5 * strength})`);
    halo.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = halo;
    ctx.fillRect(cx - 26, cy - 26, 52, 52);
    const n = Math.round(26 * strength);
    for (let i = 0; i < n; i++) {
      const a = rand() * Math.PI * 2;
      const d = rand() * rand() * (8 + strength * 12);
      ctx.fillStyle = `rgba(255,214,150,${0.35 + rand() * 0.55})`;
      ctx.fillRect(cx + Math.cos(a) * d, cy + Math.sin(a) * d * 0.6, 1.2, 1.2);
    }
  }
  return toTexture(canvas, true);
};

/** Cloud layer — wispy streaks + storm swirls on transparency. Rendered
    on its own slightly-larger sphere for parallax. */
export const makeEarthCloudsTexture = (w = 1024, h = 512) => {
  const { canvas, ctx } = makeCanvas(w, h);
  const rand = makeRand(5566);
  // trade-wind streaks
  for (let i = 0; i < 90; i++) {
    const y = h * (0.08 + rand() * 0.84);
    const x = rand() * w;
    const len = w * (0.03 + rand() * 0.1);
    const thick = 2 + rand() * 7;
    const g = ctx.createLinearGradient(x - len, y, x + len, y);
    g.addColorStop(0, "rgba(255,255,255,0)");
    g.addColorStop(0.5, `rgba(255,255,255,${0.18 + rand() * 0.3})`);
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate((rand() - 0.5) * 0.25);
    ctx.beginPath();
    ctx.ellipse(0, 0, len, thick, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  // storm swirls
  for (let i = 0; i < 14; i++) {
    const cx = rand() * w;
    const cy = h * (0.15 + rand() * 0.7);
    const R = 10 + rand() * 26;
    for (let aDeg = 0; aDeg < 540; aDeg += 24) {
      const a = (aDeg * Math.PI) / 180;
      const r = R * (aDeg / 540);
      ctx.fillStyle = `rgba(255,255,255,${0.22 * (1 - aDeg / 600)})`;
      ctx.beginPath();
      ctx.ellipse(cx + Math.cos(a) * r, cy + Math.sin(a) * r * 0.62, R * 0.3, R * 0.14, a, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  return toTexture(canvas, true);
};
